import { formatDateString } from "@cp/toolkit";
import { Icon, StateIcon } from "@qw/ui";
import { useMemo, useState } from "react";
import type { ItemWithInsured } from "./use-pinned-insureds";

export type BookRowVariant = "default" | "renewal";

/**
 * The fixed-width leading column the names line up against. Exported so a row
 * with no book item behind it can hold the same space without copying the list.
 */
export const BOOK_ROW_GUTTER_CLASSNAME =
  "flex font-mono font-semibold gap-2 h-6 items-center pl-2 text-2xs text-center text-muted-foreground group-aria-current:text-primary";

/**
 * The state icon and date every book row leads with, in the fixed-width column
 * the names line up against.
 *
 * Shared rather than inlined in `InsuredLink` because `SelectedInsuredRow` has to
 * be the same row — a way back that renders its own approximation of this would
 * drift the first time either side changed.
 */
export const BookRowGutter = <T,>({
  item,
  variant = "default",
}: {
  item: ItemWithInsured<T>;
  variant?: BookRowVariant;
}) => {
  const { effectiveDate, desiredEffectiveDate, expirationDate, state } = item;
  const [now] = useState(() => Date.now());

  const dateDisplay = useMemo(() => {
    if (variant === "renewal") {
      const renewalDate = expirationDate || desiredEffectiveDate;
      if (renewalDate) {
        const daysToExpiration = Math.round((new Date(renewalDate).getTime() - now) / (1000 * 60 * 60 * 24));
        return `${daysToExpiration}d`;
      }
      return "–";
    }
    const date = effectiveDate || desiredEffectiveDate;
    return date ? formatDateString(date, "MMM dd") : "–";
  }, [variant, expirationDate, desiredEffectiveDate, effectiveDate, now]);

  return (
    <span className={BOOK_ROW_GUTTER_CLASSNAME}>
      {variant === "renewal" ? (
        <Icon icon="schedule" className="group-aria-current:text-inherit" />
      ) : (
        <StateIcon state={state} className="group-aria-current:text-inherit" />
      )}
      {dateDisplay}
    </span>
  );
};
